// Deep Research — multi-step research that ends in a cited report.
//
// Orchestrated in the client on top of ordinary /api/chat calls (via the
// injected `ask`), so every step inherits the server's model gating,
// metering, compliance routing and PII redaction unchanged.
//
//   1. plan      — break the question into focused sub-questions
//   2. research  — per sub-question: web search and/or knowledge-base
//                  lookup, each as its own call so [n] citations map to
//                  exactly one source list
//   3. gaps      — (standard/deep) ask what's still missing, research it
//   4. report    — synthesise a long markdown report citing the global
//                  source list; the Sources section is appended here, not
//                  written by the model, so it can't invent URLs.
//
// `ask(prompt, { webSearch, kbId, maxTokens })` → { text, sources: [url],
// kbSources: [{ n, doc }] }. Pure apart from `ask`, so it's unit-tested
// with a stub.

export const DEPTHS = {
  quick:    { label: "Quick",    questions: 3, followups: 0, concurrency: 3 },
  standard: { label: "Standard", questions: 5, followups: 2, concurrency: 3 },
  deep:     { label: "Deep",     questions: 7, followups: 3, concurrency: 3 },
};
export const RESEARCH_COMMAND = /^\/research\s+/i;

export class ResearchStopped extends Error {
  constructor() { super("Research stopped"); this.stopped = true; }
}

export function buildPlanPrompt(question, n) {
  return `You are planning a research report. Break the request below into ${n} focused, non-overlapping research sub-questions that together fully answer it. Each must be specific enough to search for.\n\nReply with ONLY a JSON array of ${n} strings, no prose.\n\nRequest: ${question}`;
}

// Robustly pull a list of questions out of a model reply: a JSON array
// anywhere in the text, else numbered/bulleted lines.
export function parseQuestionList(text, max) {
  const s = String(text || "");
  const m = s.match(/\[[\s\S]*\]/);
  if (m) {
    try {
      const arr = JSON.parse(m[0]);
      if (Array.isArray(arr)) {
        const out = arr.map((x) => (typeof x === "string" ? x : x && (x.question || x.q) || "")).map((x) => x.trim()).filter(Boolean);
        if (out.length) return dedupe(out).slice(0, max);
      }
    } catch {}
  }
  const lines = s.split(/\n+/).map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, "").replace(/^["']|["'],?$/g, "").trim())
    .filter((l) => l.length > 8 && /[?a-z]/i.test(l) && !/^(here|sure|okay)\b/i.test(l));
  return dedupe(lines).slice(0, max);
}
function dedupe(list) {
  const seen = new Set();
  return list.filter((x) => { const k = x.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
}

export function buildWebPrompt(question, sub) {
  return `Research this sub-question for a report on "${question}":\n\n${sub}\n\nUsing the web search results provided, write the key findings as concise bullet points with concrete facts, figures and dates. Cite every claim with the bracket number of its source, like [1] or [2][3]. Note disagreements between sources. If the results don't answer it, say so plainly.`;
}
export function buildKbPrompt(question, sub) {
  return `Research this sub-question for a report on "${question}":\n\n${sub}\n\nUsing ONLY the knowledge-base excerpts provided, write the key findings as concise bullet points. Cite every claim with the excerpt's bracket number, like [1]. If the excerpts don't cover it, reply exactly: NO_RELEVANT_KB_CONTENT`;
}
export function buildGapPrompt(question, findings, n) {
  const digest = findings.map((f, i) => `### ${i + 1}. ${f.question}\n${f.text.slice(0, 1200)}`).join("\n\n");
  return `Research so far for: "${question}"\n\n${digest}\n\nWhat are the ${n} most important gaps, open questions or claims that need verification before a thorough report can be written? Reply with ONLY a JSON array of ${n} specific search-ready questions.`;
}
export function buildReportPrompt(question, findings, sources) {
  const notes = findings.map((f, i) => `### Finding set ${i + 1}: ${f.question}\n${f.text}`).join("\n\n");
  const list = sources.map((s) => `[${s.n}] ${s.label}`).join("\n");
  return `Write a thorough, well-structured research report answering:\n\n"${question}"\n\nBase it ONLY on the research notes below. Requirements:\n- Start with a short **Executive summary** (3–5 bullets), then organised sections with ## headings, then **Key takeaways**.\n- Cite claims inline with the global source numbers exactly as they appear in the notes, e.g. [3] or [2][7]. Never invent a source number.\n- Prefer specifics (numbers, dates, names). Flag uncertainty or conflicting evidence explicitly.\n- Do NOT write a Sources/References section — it is appended automatically.\n\n# Research notes\n${notes}\n\n# Source numbers available\n${list}`;
}

// Global, deduplicated source registry.
export function createSourceRegistry() {
  const list = [];
  const byKey = new Map();
  return {
    list,
    add(kind, value) {
      const key = kind + "|" + String(value).trim().toLowerCase();
      if (byKey.has(key)) return byKey.get(key);
      const n = list.length + 1;
      const label = kind === "kb" ? `Knowledge base · ${value}` : String(value);
      list.push({ n, kind, value: String(value), label });
      byKey.set(key, n);
      return n;
    },
  };
}

// Rewrite local [k] citations to global numbers. `localToGlobal[k-1]` is
// the global number for local source k; unknown numbers are dropped.
export function remapCitations(text, localToGlobal) {
  return String(text || "").replace(/\[(\d{1,3}(?:\s*,\s*\d{1,3})*)\]/g, (whole, inner) => {
    const nums = inner.split(/\s*,\s*/).map((x) => localToGlobal[Number(x) - 1]).filter(Boolean);
    return nums.length ? nums.map((n) => `[${n}]`).join("") : "";
  });
}

export function stripModelSources(report) {
  return String(report || "").replace(/\n#{1,3}\s*(sources|references|bibliography)\b[\s\S]*$/i, "").trim();
}

export function formatSourcesSection(sources) {
  if (!sources.length) return "";
  const lines = sources.map((s) => {
    if (s.kind === "web") {
      let host = s.value;
      try { host = new URL(s.value).hostname.replace(/^www\./, ""); } catch {}
      return `${s.n}. [${host}](${s.value})`;
    }
    return `${s.n}. ${s.label}`;
  });
  return `\n\n## Sources\n\n${lines.join("\n")}`;
}

async function pool(items, size, fn) {
  const out = new Array(items.length);
  let i = 0;
  const workers = Array.from({ length: Math.min(size, items.length) }, async () => {
    while (i < items.length) { const idx = i++; out[idx] = await fn(items[idx], idx); }
  });
  await Promise.all(workers);
  return out;
}

const STOPPED_TEXT = "(Stopped)";

export async function runDeepResearch({ question, depth = "standard", useWeb = true, kbId = null, ask, onProgress, isStopped, sleep = (ms) => new Promise((r) => setTimeout(r, ms)), retryDelayMs = 15000 }) {
  const cfg = DEPTHS[depth] || DEPTHS.standard;
  const stopped = () => (isStopped && isStopped());
  const state = { question, depth, phase: "planning", steps: [], sourceCount: 0, startedAt: Date.now() };
  const emit = () => { try { onProgress && onProgress(JSON.parse(JSON.stringify(state))); } catch {} };
  const call = async (prompt, opts) => {
    if (stopped()) throw new ResearchStopped();
    let r;
    for (let attempt = 0; ; attempt++) {
      try { r = await ask(prompt, opts); break; }
      catch (e) {
        // A Stop click aborts the in-flight fetch; report it as a stop.
        if (stopped() || (e && e.name === "AbortError")) throw new ResearchStopped();
        // /api/chat is rate limited per user; back off instead of failing.
        if (attempt < 3 && /too many requests|429/i.test(String((e && e.message) || e))) {
          await sleep(retryDelayMs * (attempt + 1));
          if (stopped()) throw new ResearchStopped();
          continue;
        }
        throw e;
      }
    }
    if (stopped() || (r && r.text && r.text.trim() === STOPPED_TEXT)) throw new ResearchStopped();
    return { text: (r && r.text) || "", sources: (r && r.sources) || [], kbSources: (r && r.kbSources) || [] };
  };
  if (!useWeb && !kbId) throw new Error("Deep research needs web search or a knowledge base");
  emit();

  // 1. Plan
  const plan = await call(buildPlanPrompt(question, cfg.questions), { webSearch: false, kbId: null, maxTokens: 800 });
  let subs = parseQuestionList(plan.text, cfg.questions);
  if (!subs.length) subs = [question];
  const registry = createSourceRegistry();
  const findings = [];

  const researchOne = async (sub) => {
    const step = { question: sub, status: "running", sources: 0 };
    state.steps.push(step);
    emit();
    const parts = [];
    try {
      if (kbId) {
        const r = await call(buildKbPrompt(question, sub), { webSearch: false, kbId, maxTokens: 1200 });
        const map = (r.kbSources || []).map((k) => registry.add("kb", k.doc || ("document " + k.n)));
        if (!/NO_RELEVANT_KB_CONTENT/.test(r.text) && r.text.trim()) { parts.push(remapCitations(r.text, map)); step.sources += map.length; }
      }
      if (useWeb) {
        const r = await call(buildWebPrompt(question, sub), { webSearch: true, kbId: null, maxTokens: 1400 });
        const map = (r.sources || []).map((u) => registry.add("web", u));
        if (r.text.trim()) { parts.push(remapCitations(r.text, map)); step.sources += map.length; }
      }
      step.status = parts.length ? "done" : "empty";
    } catch (e) {
      if (e && e.stopped) throw e;
      step.status = "error";
      step.error = String((e && e.message) || e).slice(0, 200);
    }
    state.sourceCount = registry.list.length;
    emit();
    if (parts.length) findings.push({ question: sub, text: parts.join("\n\n") });
  };

  // 2. Research
  state.phase = "researching";
  emit();
  await pool(subs, cfg.concurrency, researchOne);

  // 3. Gaps
  if (cfg.followups > 0 && findings.length) {
    state.phase = "gaps";
    emit();
    try {
      const g = await call(buildGapPrompt(question, findings, cfg.followups), { webSearch: false, kbId: null, maxTokens: 600 });
      const known = new Set(subs.map((x) => x.toLowerCase()));
      const follow = parseQuestionList(g.text, cfg.followups).filter((x) => !known.has(x.toLowerCase()));
      if (follow.length) {
        state.phase = "researching";
        emit();
        await pool(follow, cfg.concurrency, researchOne);
      }
    } catch (e) { if (e && e.stopped) throw e; }
  }

  if (!findings.length) throw new Error("Research found nothing usable — try rephrasing or turning on web search.");

  // 4. Report
  state.phase = "writing";
  emit();
  const rep = await call(buildReportPrompt(question, findings, registry.list), { webSearch: false, kbId: null, maxTokens: 6000 });
  const body = stripModelSources(rep.text);
  // Only list sources the report (or notes) actually cite, renumbered 1..n.
  const cited = new Set([...body.matchAll(/\[(\d{1,3})\]/g)].map((m) => Number(m[1])));
  const kept = registry.list.filter((s) => cited.has(s.n));
  const renum = new Map(kept.map((s, i) => [s.n, i + 1]));
  const finalBody = body.replace(/\[(\d{1,3})\]/g, (w, d) => (renum.has(Number(d)) ? `[${renum.get(Number(d))}]` : ""));
  const finalSources = kept.map((s, i) => ({ ...s, n: i + 1 }));
  const report = `${finalBody}${formatSourcesSection(finalSources)}`;
  state.phase = "done";
  state.sourceCount = finalSources.length;
  state.finishedAt = Date.now();
  emit();
  return { report, sources: finalSources, steps: state.steps, durationMs: state.finishedAt - state.startedAt };
}
